<template>
  <a-modal
    :open="open"
    centered
    :maskClosable="false"
    :width="450"
    :keyboard="false"
    @cancel="handleClose"
  >
    <template #title>
      {{ isPrepaid ? "Comprar slots" : "Agregar slots" }}

      <a-tag color="blue">Slots disponibles: {{ remainingSlots }}</a-tag>
    </template>

    <div class="content">
      <img src="../../assets/payment-cart.png" alt="cart icon" width="90" />

      <h3 v-if="isPrepaid">Alcanzaste el límite de tu plan</h3>
      <h3 v-else>Todos tus slots están ocupados</h3>
      <p style="margin-bottom: 20px; text-align: center;">
        <template v-if="isPrepaid">
          ¿Tu equipo de trabajo crece? ¡No te preocupes! Añade usuarios adicionales a tu pago
          mensual y paga por estos usuarios solo el prorrateo según el número de días que falte para
          tu fecha de facturación.
        </template>

        <template v-else>
          ¡Amplía tu equipo sin preocupaciones! Agrega slots a tu empresa y el importe
          correspondiente se incluirá en tu factura mensual al final del mes, independientemente de
          cuándo los añadas.
        </template>
      </p>

      <a-form layout="vertical">
        <a-form-item
          :label="
            isPrepaid ? 'Número de slots que desea comprar' : 'Número de slots que desea agregar'
          "
        >
          <a-input-number style="width: 100%" v-model:value="slotsQuantity" :steps="0" :min="0" />
          <span class="help">
            <template v-if="isPrepaid && slotsInfo?.tax">
              Faltan {{ Math.abs(dayjs().diff(slotsInfo?.date_payment, "day")) }} días para tu facturación. 
              El precio por slot es {{ slotsInfo?.slot_price_now.toFixed(2) }}. 
              Se sumará el IGV aparte.
            </template>
            <template v-else-if="isPrepaid">
              Faltan {{ Math.abs(dayjs().diff(slotsInfo?.date_payment, "day")) }} días para tu
              facturación. El precio por slot es tan solo de ${{
                slotsInfo?.slot_price_now.toFixed(2)
              }}.
            </template>
            <template v-else-if="isPostpaid && slotsInfo?.tax">
              El precio por slot es ${{ slotsInfo?.slot_price.toFixed(2) }}. 
              Se sumará el IGV aparte. 
              El total será cobrado en tu fecha de facturación.
            </template>
            <template v-else>
              El precio por slot es de ${{ slotsInfo?.slot_price.toFixed(2) }}. Será cobrado en tu
              fecha de facturación.
            </template>
          </span>
        </a-form-item>
      </a-form>

      <a-divider class="divider" />

      <div v-if="slotsInfo?.tax && totalPrice" class="section_price__details">
        <div class="section_price__details--item">
          <span>Precio de slots</span>
          <span>${{ totalPrice }}</span>
        </div>
        <div class="section_price__details--item">
          <span>IGV (18%)</span>
          <span>${{ (totalPrice * slotsInfo?.tax).toFixed(2) }}</span>
        </div>
  
        <a-divider class="divider" />
      </div>

      <p class="total">
        <template v-if="isPrepaid"> Total: ${{ totalIncludeIgv }} </template>

        <template v-else> Total slots: {{ slotsQuantity }} </template>
      </p>
    </div>

    <template #footer>
      <footer class="footer">
        <a-button @click="handleClose">Cancelar</a-button>

        <PayUPaymentButton
          v-if="isPrepaid"
          :amount="totalIncludeIgv"
          :price="formattedPrice"
          :disabled="!totalPrice"
          :count="slotsQuantity"
          name="Comprar slots"
          :type="ProductType.Slot"
          @success="
            handleClose();
            emit('update');
          "
        />

        <a-button
          v-else
          type="primary"
          :disabled="!slotsQuantity"
          @click="handleAddSlots"
          :loading="loading"
        >
          Agregar slots
        </a-button>
      </footer>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import dayjs from "dayjs";
import { message } from "ant-design-vue";

import { ISlots } from "@/@types/settings/slots";
import { ProductType } from "@/@types/settings/billing";

import PayUPaymentButton from "@/app/shared/components/buttons/PayUPaymentButton.vue";
import SlotManager from "@/app/settings/services/slots/SlotManager";

const igvFactor = 1.18;
const props = defineProps<{
  open: boolean;
  slotsInfo: ISlots | undefined;
  remainingSlots: number;
  neededSlots: number;
}>();

const emit = defineEmits<{ "update:open": [open: boolean]; update: [] }>();

const slotsQuantity = ref(props.neededSlots);

const isPrepaid = computed(() => props.slotsInfo?.slot_type_billing === "Prepago");
const isPostpaid = computed(() => props.slotsInfo?.slot_type_billing === "Postpago");

const totalPrice = computed(() => {
  const pricePerSlot = isPrepaid.value
    ? props.slotsInfo?.slot_price_now ?? 0
    : props.slotsInfo?.slot_price ?? 0;

  const total = slotsQuantity.value * pricePerSlot;

  return Number(total.toFixed(2));
});

const totalIncludeIgv = computed(() => {
  const totalIncludeIgvValue = totalPrice.value * (props.slotsInfo?.tax ? igvFactor : 1);
  return Number(totalIncludeIgvValue.toFixed(2));
});

const handlePriceFormatt = () => Number((props.slotsInfo?.slot_price_now ?? 0).toFixed(2));

const formattedPrice = computed(() => handlePriceFormatt());

const loading = ref(false);
const manager = new SlotManager();

const handleAddSlots = async () => {
  loading.value = true;

  try {
    await manager.buySlots(Number(props.slotsInfo?.slot_price), slotsQuantity.value);
    message.success("Se han comprado slots con éxito");
    handleClose();
  } catch (error) {
    console.error(error);
    message.error("Ha ocurrido un error");
  }

  loading.value = false;
};

const handleClose = () => emit("update:open", false);
</script>

<style scoped lang="sass">
p
  color: $gray-9

.content
  display: flex
  flex-direction: column
  align-items: center

  img
    margin: 20px 0

.divider
  background-color: $gray-4
  margin: 12px 0

.ant-form-item
  margin: 0

.help
  font-size: 12px
  color: $gray-7

.total
  width: 100%
  text-align: right
  font-size: 20px

.footer
  display: flex
  justify-content: flex-end

:deep(.payment-button)
  width: auto

.section_price
  &__details
    width: 100%
    text-align: right
    &--item
      display: flex
      align-items: center
      justify-content: end
      text-align: right
      gap: 24px
      color: #00000073
</style>
